/**
 * Cross-process lease for session-summary delivery.
 *
 * Claude Code and Codex can end sessions concurrently. Each terminal hook may
 * request a detached flush, but only one worker may walk the immutable queue at
 * a time. The lock contains no credentials or session content.
 */

import { randomUUID } from "node:crypto";
import {
  closeSync,
  fsyncSync,
  mkdirSync,
  openSync,
  readFileSync,
  renameSync,
  statSync,
  unlinkSync,
  writeSync,
} from "node:fs";
import { join } from "node:path";

export const SESSION_SUMMARY_FLUSH_LOCK_NAME = ".flush.lock";
export const SESSION_SUMMARY_FLUSH_MAX_AGE_MS = 2 * 60 * 60 * 1_000;
const MALFORMED_LOCK_GRACE_MS = 5 * 60 * 1_000;

interface SessionSummaryFlushLockRecord {
  token: string;
  pid: number;
  claimed_at: string;
}

export interface SessionSummaryFlushLease {
  acquired: true;
  path: string;
  release(): void;
}

export interface SessionSummaryFlushLeaseUnavailable {
  acquired: false;
  reason: "busy" | "unavailable";
}

export type SessionSummaryFlushLeaseResult =
  | SessionSummaryFlushLease
  | SessionSummaryFlushLeaseUnavailable;

export interface ClaimSessionSummaryFlushLeaseOptions {
  now?: Date;
  pid?: number;
  token?: string;
  isProcessAlive?: (pid: number) => boolean;
}

function errorCode(error: unknown): string | null {
  return error && typeof error === "object" && "code" in error
    ? String(error.code)
    : null;
}

function readLock(path: string): SessionSummaryFlushLockRecord | null {
  try {
    const value = JSON.parse(readFileSync(path, "utf8")) as unknown;
    if (!value || typeof value !== "object" || Array.isArray(value)) return null;
    const record = value as Record<string, unknown>;
    return typeof record.token === "string" &&
      typeof record.pid === "number" &&
      Number.isInteger(record.pid) &&
      typeof record.claimed_at === "string"
      ? {
          token: record.token,
          pid: record.pid,
          claimed_at: record.claimed_at,
        }
      : null;
  } catch {
    return null;
  }
}

function processIsAlive(pid: number): boolean {
  if (!Number.isInteger(pid) || pid <= 0) return false;
  try {
    process.kill(pid, 0);
    return true;
  } catch (error) {
    return errorCode(error) === "EPERM";
  }
}

function lockAgeMs(path: string, now: Date): number | null {
  try {
    return Math.max(0, now.getTime() - statSync(path).mtimeMs);
  } catch {
    return null;
  }
}

function shouldReclaimLock(
  path: string,
  now: Date,
  isProcessAlive: (pid: number) => boolean,
): boolean {
  const age = lockAgeMs(path, now);
  if (age === null) return true;
  if (age >= SESSION_SUMMARY_FLUSH_MAX_AGE_MS) return true;

  const record = readLock(path);
  if (!record) return age >= MALFORMED_LOCK_GRACE_MS;
  return !isProcessAlive(record.pid);
}

function writeLock(
  path: string,
  record: SessionSummaryFlushLockRecord,
): "acquired" | "exists" | "unavailable" {
  let descriptor: number | null = null;
  try {
    descriptor = openSync(path, "wx", 0o600);
    writeSync(descriptor, JSON.stringify(record), undefined, "utf8");
    fsyncSync(descriptor);
    return "acquired";
  } catch (error) {
    return errorCode(error) === "EEXIST" ? "exists" : "unavailable";
  } finally {
    if (descriptor !== null) closeSync(descriptor);
  }
}

function releaseOwnedLock(path: string, token: string): void {
  try {
    if (readLock(path)?.token === token) unlinkSync(path);
  } catch {
    // A later worker may already own the path. Never remove an unknown lock.
  }
}

export function claimSessionSummaryFlushLease(
  queueDir: string,
  options: ClaimSessionSummaryFlushLeaseOptions = {},
): SessionSummaryFlushLeaseResult {
  const now = options.now ?? new Date();
  const pid = options.pid ?? process.pid;
  const token = options.token ?? randomUUID();
  const isProcessAlive = options.isProcessAlive ?? processIsAlive;
  const path = join(queueDir, SESSION_SUMMARY_FLUSH_LOCK_NAME);
  const record = { token, pid, claimed_at: now.toISOString() };

  try {
    mkdirSync(queueDir, { recursive: true, mode: 0o700 });
  } catch {
    return { acquired: false, reason: "unavailable" };
  }

  const firstAttempt = writeLock(path, record);
  if (firstAttempt === "acquired") {
    return {
      acquired: true,
      path,
      release: () => releaseOwnedLock(path, token),
    };
  }
  if (firstAttempt === "unavailable") {
    return { acquired: false, reason: "unavailable" };
  }
  if (!shouldReclaimLock(path, now, isProcessAlive)) {
    return { acquired: false, reason: "busy" };
  }

  const stalePath = `${path}.stale-${token}`;
  try {
    renameSync(path, stalePath);
    unlinkSync(stalePath);
  } catch {
    return { acquired: false, reason: "busy" };
  }

  const retry = writeLock(path, record);
  if (retry !== "acquired") {
    return {
      acquired: false,
      reason: retry === "exists" ? "busy" : "unavailable",
    };
  }

  return {
    acquired: true,
    path,
    release: () => releaseOwnedLock(path, token),
  };
}
